// @ts-nocheck

/**
 * City Manager Service
 * 
 * Handles all city manager related operations including:
 * - Fetching assigned cities for a user
 * - Getting city metrics and statistics
 * - Managing city manager assignments
 */

import { db } from '../firebase';
import firebase from 'firebase/compat/app';

/**
 * Get all city manager assignments for a user
 * @param {string} userId - The user ID to get assignments for
 * @returns {Promise<Array>} - Array of city manager assignments
 */
export const getUserCityAssignments = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  try {
    const snapshot = await db.collection('cityManagers')
      .where('userId', '==', userId)
      .where('isActive', '==', true)
      .get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      assignedAt: doc.data().assignedAt?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    }));
  } catch (error) {
    console.error('Error fetching city assignments:', error);
    throw error;
  }
};

/**
 * Get city details by ID
 * @param {string} cityId - The city document ID
 * @returns {Promise<Object|null>} - City data or null if not found
 */
export const getCityById = async (cityId) => {
  if (!cityId) {
    throw new Error('City ID is required');
  }

  try {
    const doc = await db.collection('msi3PopulationCenters').doc(cityId).get();
    
    if (!doc.exists) {
      return null;
    }

    return {
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    };
  } catch (error) {
    console.error('Error fetching city:', error);
    throw error;
  }
};

/**
 * Get all cities (for role_owner or admin)
 * @param {Object} [options] - Query options
 * @param {string} [options.status] - Filter by status ('active', 'planned', 'all')
 * @param {string} [options.countryCode] - Filter by country code
 * @returns {Promise<Array>} - Array of cities
 */
export const getAllCities = async (options) => {
  const { status = 'all', countryCode = null } = options ?? {};

  try {
    let query = db.collection('msi3PopulationCenters');

    if (status !== 'all') {
      query = query.where('status', '==', status);
    }

    if (countryCode) {
      query = query.where('countryCode', '==', countryCode);
    }

    query = query.orderBy('sequenceNumber', 'asc');

    const snapshot = await query.get();

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate(),
      updatedAt: doc.data().updatedAt?.toDate(),
    }));
  } catch (error) {
    console.error('Error fetching cities:', error);
    throw error;
  }
};

/**
 * Get city metrics (enrollments, facilities, penetration)
 * @param {string} cityId - The city document ID
 * @returns {Promise<Object>} - City metrics object
 */
export const getCityMetrics = async (cityId) => {
  if (!cityId) {
    throw new Error('City ID is required');
  }

  try {
    // Get city data
    const cityDoc = await db.collection('msi3PopulationCenters').doc(cityId).get();
    
    if (!cityDoc.exists) {
      throw new Error('City not found');
    }

    const cityData = cityDoc.data();

    // Get patient enrollments for this city
    const enrollmentsSnapshot = await db.collection('msi3PatientEnrollments')
      .where('enrollmentCityId', '==', cityId)
      .get();

    // Count by subscription tier
    const tierCounts = {
      free: 0,
      small: 0,
      medium: 0,
      large: 0,
    };

    enrollmentsSnapshot.docs.forEach(doc => {
      const tier = doc.data().subscriptionTier || 'free';
      if (tierCounts.hasOwnProperty(tier)) {
        tierCounts[tier]++;
      }
    });

    const totalEnrolled = enrollmentsSnapshot.size;
    const paidEnrolled = tierCounts.small + tierCounts.medium + tierCounts.large;

    // Get facility partners for this city
    const facilitiesSnapshot = await db.collection('msi3FacilityPartners')
      .where('cityId', '==', cityId)
      .get();

    const facilityStats = {
      total: facilitiesSnapshot.size,
      byStatus: {
        prospect: 0,
        negotiating: 0,
        signed: 0,
        active: 0,
      },
      byTier: {
        tier1: 0,
        tier2: 0,
        tier3: 0,
      },
    };

    facilitiesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      if (facilityStats.byStatus.hasOwnProperty(data.partnershipStatus)) {
        facilityStats.byStatus[data.partnershipStatus]++;
      }
      if (data.tier && facilityStats.byTier.hasOwnProperty(`tier${data.tier}`)) {
        facilityStats.byTier[`tier${data.tier}`]++;
      }
    });

    // Calculate market penetration
    const addressableMarket = cityData.estimatedChronicDiseasePatients || cityData.population * 0.1;
    const penetrationPercentage = addressableMarket > 0 
      ? (totalEnrolled / addressableMarket) * 100 
      : 0;

    // Determine penetration tier for revenue share
    let penetrationTier = '0-33%';
    let revenueShareRate = 75;
    if (penetrationPercentage >= 67) {
      penetrationTier = '67-100%';
      revenueShareRate = 25;
    } else if (penetrationPercentage >= 34) {
      penetrationTier = '34-66%';
      revenueShareRate = 50;
    }

    return {
      city: {
        id: cityDoc.id,
        name: cityData.cityName,
        displayName: cityData.displayName || cityData.cityName,
        county: cityData.adminDivision1,
        region: cityData.region,
        population: cityData.population,
        addressableMarket,
        status: cityData.status,
        sequenceNumber: cityData.sequenceNumber,
      },
      enrollments: {
        total: totalEnrolled,
        paid: paidEnrolled,
        free: tierCounts.free,
        byTier: tierCounts,
      },
      facilities: facilityStats,
      penetration: {
        percentage: parseFloat(penetrationPercentage.toFixed(2)),
        tier: penetrationTier,
        revenueShareRate,
      },
    };
  } catch (error) {
    console.error('Error fetching city metrics:', error);
    throw error;
  }
};

/**
 * Get all city managers for a specific city
 * @param {string} cityId - The city document ID
 * @returns {Promise<Array>} - Array of city manager assignments
 */
export const getCityManagers = async (cityId) => {
  if (!cityId) {
    throw new Error('City ID is required');
  }

  try {
    const snapshot = await db.collection('cityManagers')
      .where('cityId', '==', cityId)
      .where('isActive', '==', true)
      .get();

    // Get user details for each manager
    const managers = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const managerData = doc.data();
        let userData = null;

        try {
          const userDoc = await db.collection('users').doc(managerData.userId).get();
          if (userDoc.exists) {
            userData = {
              name: userDoc.data().name,
              email: userDoc.data().email,
            };
          }
        } catch (e) {
          console.warn('Could not fetch user data for manager:', managerData.userId);
        }

        return {
          id: doc.id,
          ...managerData,
          user: userData,
          assignedAt: managerData.assignedAt?.toDate(),
        };
      })
    );

    return managers;
  } catch (error) {
    console.error('Error fetching city managers:', error);
    throw error;
  }
};

/**
 * Deactivate a city manager assignment
 * @param {string} assignmentId - The assignment document ID
 * @param {string} deactivatedBy - The user ID deactivating the assignment
 * @returns {Promise<void>}
 */
export const deactivateCityManager = async (assignmentId, deactivatedBy) => {
  if (!assignmentId) {
    throw new Error('Assignment ID is required');
  }

  try {
    await db.collection('cityManagers').doc(assignmentId).update({
      isActive: false,
      deactivatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      deactivatedBy,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });
  } catch (error) {
    console.error('Error deactivating city manager:', error);
    throw error;
  }
};

/**
 * Get recent activity for a city (enrollments, contacts, etc.)
 * @param {string} cityId - The city document ID
 * @param {number} limit - Max number of activities to return
 * @returns {Promise<Array>} - Array of activity items
 */
export const getCityRecentActivity = async (cityId, limit = 10) => {
  if (!cityId) {
    throw new Error('City ID is required');
  }

  try {
    const activities = [];

    // Get recent enrollments
    const enrollmentsSnapshot = await db.collection('msi3PatientEnrollments')
      .where('enrollmentCityId', '==', cityId)
      .orderBy('enrollmentDate', 'desc')
      .limit(limit)
      .get();

    enrollmentsSnapshot.docs.forEach(doc => {
      const data = doc.data();
      activities.push({
        type: 'enrollment',
        id: doc.id,
        timestamp: data.enrollmentDate?.toDate(),
        data: {
          tier: data.subscriptionTier,
          facilityId: data.enrollingFacilityId,
        },
      });
    });

    // Get recent clinical contacts
    // Note: contactsSnapshot query prepared for future use when denormalized field is added
    // const contactsSnapshot = await db.collection('msi3ClinicalContacts')
    //   .orderBy('contactDate', 'desc')
    //   .limit(limit * 2) // Get more since we'll filter by city
    //   .get();

    // Filter contacts by city (need to check facility's city)
    // This is a simplified version - in production you might want a denormalized field
    
    // Sort all activities by timestamp
    activities.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

    return activities.slice(0, limit);
  } catch (error) {
    console.error('Error fetching city activity:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time updates for city metrics
 * @param {string} cityId - The city document ID
 * @param {Function} callback - Callback function for updates
 * @returns {Function} - Unsubscribe function
 */
export const subscribeToCityUpdates = (cityId, callback) => {
  if (!cityId) {
    throw new Error('City ID is required');
  }

  // Subscribe to city document
  const unsubCity = db.collection('msi3PopulationCenters')
    .doc(cityId)
    .onSnapshot(
      (doc) => {
        if (doc.exists) {
          callback({
            type: 'city',
            data: { id: doc.id, ...doc.data() },
          });
        }
      },
      (error) => {
        console.error('Error in city subscription:', error);
      }
    );

  // Subscribe to enrollments for this city
  const unsubEnrollments = db.collection('msi3PatientEnrollments')
    .where('enrollmentCityId', '==', cityId)
    .onSnapshot(
      (snapshot) => {
        callback({
          type: 'enrollments',
          data: {
            count: snapshot.size,
            docs: snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          },
        });
      },
      (error) => {
        console.error('Error in enrollments subscription:', error);
      }
    );

  // Return combined unsubscribe function
  return () => {
    unsubCity();
    unsubEnrollments();
  };
};

const cityManagerServiceExports = {
  getUserCityAssignments,
  getCityById,
  getAllCities,
  getCityMetrics,
  getCityManagers,
  deactivateCityManager,
  getCityRecentActivity,
  subscribeToCityUpdates,
};

export default cityManagerServiceExports;

